import React from "react";
import NavBar from "../components/Navbar/NavBar";
import Footer from "../components/Footer";
import { useDocTitle } from "../components/CustomHook";
import { Link } from "react-router-dom";

/*
 * ZelConnect Remote — supervised remote support page.
 *
 * SAFEGUARD PRINCIPLES baked into the copy (non-negotiable):
 *  - Member-initiated only; we never connect uninvited
 *  - One-time session code, single use, expires
 *  - Member controls End Session at all times
 *  - No unattended/persistent access, ever
 *  - Anti-scam education: we will NEVER cold-call or email first
 */

const safeguards = [
  {
    icon: "🙋",
    title: "You start it — always",
    desc: "A session only begins after YOU request it and click Allow. We can never connect to your computer uninvited.",
  },
  {
    icon: "🔢",
    title: "One-time code",
    desc: "Each session uses a unique code that works once and expires in minutes. No code, no connection.",
  },
  {
    icon: "🛑",
    title: "Your kill switch",
    desc: "A big red End Session button is on your screen the whole time. One click ends everything instantly.",
  },
  {
    icon: "👀",
    title: "You see everything",
    desc: "You watch the entire session on your own screen. Nothing happens in the dark.",
  },
  {
    icon: "🚫",
    title: "No permanent access",
    desc: "We never install 'always-on' remote access. When the session ends, the connection is gone for good.",
  },
  {
    icon: "📜",
    title: "It's on the record",
    desc: "Every session is logged: date, technician, duration. Accountability is part of the service.",
  },
];

const steps = [
  {
    n: 1,
    title: "Request a session",
    desc: "Use the request form below (or contact us). A ZelTechnology technician confirms a time with you.",
  },
  {
    n: 2,
    title: "Get your one-time code",
    desc: "At your appointment, we give you a 6-digit session code and the small helper app link.",
  },
  {
    n: 3,
    title: "Run the helper & enter the code",
    desc: "One click to run it (no installation), type in the code, then click Allow when prompted.",
  },
  {
    n: 4,
    title: "We fix, you watch",
    desc: "The technician works while you watch your own screen. Chat with them the whole time.",
  },
  {
    n: 5,
    title: "Click End Session",
    desc: "Done! You end the session yourself. The connection is destroyed — not 'kept for later'.",
  },
];

// Detailed click-by-click setup shown during a session appointment.
const setupDetails = [
  {
    n: 1,
    title: "Download the small RustDesk program",
    desc: "Go to rustdesk.com and click Download. On the download page, choose the version for your computer: Windows → 'rustdesk-…-x64.exe', Mac → '…-aarch64.dmg' (Apple) or '…-x86_64.dmg' (Intel). When you run it, if Windows shows a blue 'Windows protected your PC' message, click 'More info' → 'Run anyway' — it's safe, it's the official RustDesk program.",
  },
  {
    n: 2,
    title: "Point it at ZelTechnology's server (3 fields, once)",
    desc: "In RustDesk, click the ⋯ (three dots) next to your ID → Network → unlock. Fill in exactly: ID Server: 54.69.163.169 · Relay Server: 54.69.163.169 · Key: VDGcMdIXdGUQeCn7BqBM6lonuQDce4T3ShlW+Ev66MU= · leave 'API Server' empty. Click Apply. The status at the bottom should say 'Ready' — your technician will confirm.",
  },
  {
    n: 3,
    title: "Read us your ID and password",
    desc: "Your RustDesk window shows a 9-digit ID and a one-time password. Read both to your technician when they ask. The password changes every time you open RustDesk — that's the one-time security working.",
  },
  {
    n: 4,
    title: "Click Allow when the prompt appears",
    desc: "When your technician connects, your screen shows a permission prompt — click Allow. If your Mac asks about 'Screen Recording' permission, click Configure and toggle RustDesk on (macOS requires this for any screen-sharing app).",
  },
];

const VirtualHouseCall = () => {
  useDocTitle("ZelT | ZelConnect Remote");

  return (
    <div>
      <NavBar />
      <div className="mt-8 w-full bg-white py-12 lg:py-20">
        <div className="mx-4 max-w-5xl mx-auto">
          {/* hero */}
          <div className="text-center mb-12" data-aos="zoom-in">
            <span className="inline-block px-4 py-1 rounded-full text-xs font-bold bg-teal-100 text-blue-900 border border-teal-400 mb-4">
              SUPERVISED REMOTE SUPPORT · MEMBER SERVICE
            </span>
            <h1 className="text-4xl md:text-5xl font-extrabold text-blue-900 mb-4">
              🏠 ZelConnect Remote
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Real tech help, without leaving your couch. A ZelTechnology
              technician securely connects to your computer —{" "}
              <b className="text-blue-900">
                only with your permission, only while you watch
              </b>{" "}
              — and fixes the problem while you learn.
            </p>
          </div>

          {/* THE SCAM WARNING — critical education block */}
          <div
            className="mb-12 rounded-2xl p-6 md:p-8 border-2 border-red-300 bg-red-50"
            data-aos="zoom-in"
          >
            <h2 className="text-2xl font-extrabold text-red-700 mb-3">
              ⚠️ Read this before anything else
            </h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Fake tech-support <b>remote access is the #1 scam targeting
              seniors</b>. Scammers pretend to be Microsoft, your bank, or
              even a nonprofit — and ask to connect to your computer.
            </p>
            <ul className="space-y-2 text-gray-700">
              <li>
                🚫 <b>ZelTechnology will NEVER call, email, or pop up on your
                screen uninvited.</b> Remote sessions only happen when YOU
                request one here first.
              </li>
              <li>
                🚫 We will NEVER ask you to buy gift cards, wire money, or
                read out verification codes for anything other than a session
                you scheduled.
              </li>
              <li>
                ✅ Real sessions only start from this page, with a code YOU
                received from a technician YOU contacted.
              </li>
              <li>
                ❓ Not sure if a call is really us? Hang up and call us
                directly at <b>858-215-1048</b>. We'll never be offended —
                that's exactly what we teach.
              </li>
            </ul>
          </div>

          {/* how it works */}
          <h2 className="text-2xl font-extrabold text-blue-900 mb-6 text-center" data-aos="fade-up">
            How a ZelConnect Remote Session Works
          </h2>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5 mb-14">
            {steps.map((s) => (
              <div
                key={s.n}
                className="bg-gray-50 rounded-2xl p-5 shadow-md border-t-4 border-blue-900 text-center"
                data-aos="zoom-in"
              >
                <p className="w-9 h-9 mx-auto rounded-full bg-blue-900 text-white font-extrabold flex items-center justify-center mb-3">
                  {s.n}
                </p>
                <h3 className="font-bold text-blue-900 mb-2 text-sm">{s.title}</h3>
                <p className="text-gray-600 text-xs leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>

          {/* detailed setup instructions — shown/read during the appointment */}
          <div className="mb-14" data-aos="fade-up">
            <div className="bg-blue-900 rounded-t-2xl p-5 text-white">
              <h2 className="text-2xl font-extrabold">
                📋 Step-by-Step: Setting Up Your Computer
              </h2>
              <p className="text-gray-300 text-sm mt-1">
                These are the exact steps your technician walks through with
                you on the phone — this page is your reference during the
                appointment. (This setup happens once; future sessions are
                faster.)
              </p>
            </div>
            <div className="border-2 border-t-0 border-blue-900/20 rounded-b-2xl divide-y divide-gray-100">
              {setupDetails.map((s) => (
                <div key={s.n} className="p-5 flex gap-4">
                  <p className="flex-shrink-0 w-8 h-8 rounded-full bg-teal-400 text-blue-900 font-extrabold flex items-center justify-center">
                    {s.n}
                  </p>
                  <div>
                    <h3 className="font-bold text-blue-900 mb-1">{s.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed break-words">
                      {s.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-center text-gray-400 text-xs mt-3">
              💡 Tip: bookmark this page before your appointment so it's easy
              to find.
            </p>
          </div>

          {/* safeguards */}
          <h2 className="text-2xl font-extrabold text-blue-900 mb-6 text-center" data-aos="fade-up">
            Your Safeguards — Built In, Not Promised
          </h2>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-14">
            {safeguards.map((s) => (
              <div
                key={s.title}
                className="bg-gray-50 rounded-2xl p-5 shadow-md border-l-4 border-teal-400"
                data-aos="fade-up"
              >
                <p className="text-2xl mb-2">{s.icon}</p>
                <h3 className="font-extrabold text-blue-900 mb-1 text-sm">
                  {s.title}
                </h3>
                <p className="text-gray-600 text-xs leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>

          {/* technology note */}
          <div className="bg-gray-50 rounded-2xl p-6 mb-12 text-gray-600 text-sm leading-relaxed" data-aos="fade-up">
            <b className="text-blue-900">Under the hood:</b> sessions run
            through RustDesk, an open-source remote-access tool — hosted on
            ZelTechnology's own server. The connection is encrypted, and no
            third-party company sees your screen. Sessions require a
            technician to be present; availability is by appointment.
          </div>

          {/* request CTA */}
          <div
            className="rounded-2xl p-8 text-white text-center shadow-xl"
            style={{ background: "linear-gradient(90deg, #36395a, #1e3a8a)" }}
            data-aos="fade-up"
          >
            <h2 className="text-2xl font-extrabold mb-2">
              Request a ZelConnect Remote Session
            </h2>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              Message us with "Remote Session" and a short description of the
              problem. A technician will confirm a time and walk you through
              everything.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact?request=remote"
                className="inline-block px-6 py-3 rounded-xl bg-teal-400 text-blue-900 font-bold hover:bg-teal-300 transition"
              >
                📋 Request a Session
              </Link>
              <a
                href="tel:8582151048"
                className="inline-block px-6 py-3 rounded-xl border-2 border-white text-white font-bold hover:bg-white/10 transition"
              >
                📞 Call 858-215-1048
              </a>
            </div>
            <p className="text-gray-400 text-xs mt-5">
              Members only — not a member yet? Membership is free:{" "}
              <Link to="/membership" className="underline font-semibold">
                join here
              </Link>
              .
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default VirtualHouseCall;
